import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Coins, Gift, Users, History, Save, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

interface RewardConfig {
  id?: string;
  organization_id: string;
  is_enabled: boolean;
  coins_per_booking: number;
  coins_per_match: number;
  coins_per_tournament: number;
}

interface RewardProduct {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  coin_cost: number;
  stock: number | null;
  image_url: string | null;
  is_active: boolean;
  created_at: string;
}

interface CoinTransaction {
  id: string;
  user_id: string;
  organization_id: string;
  amount: number;
  type: string;
  description: string | null;
  created_at: string;
  reference_id: string | null;
}

export default function AdminRewards() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { user } = useAuth();
  const qc = useQueryClient();

  // Config
  const { data: config } = useQuery({
    queryKey: ["reward-config", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("reward_config" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .maybeSingle();
      return data as RewardConfig | null;
    },
    enabled: !!organization?.id,
  });

  const [configForm, setConfigForm] = useState<Partial<RewardConfig>>({});
  const currentConfig = { ...config, ...configForm };

  const saveConfigMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        organization_id: organization!.id,
        is_enabled: currentConfig.is_enabled ?? false,
        coins_per_booking: currentConfig.coins_per_booking ?? 10,
        coins_per_match: currentConfig.coins_per_match ?? 5,
        coins_per_tournament: currentConfig.coins_per_tournament ?? 20,
      };
      if (config?.id) {
        const { error } = await supabase.from("reward_config" as any).update(payload).eq("id", config.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("reward_config" as any).insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-config"] });
      setConfigForm({});
      toast.success(t("admin:toast.rewardConfigSaved"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("rewards.couldNotSave")); },
  });

  // Products
  const { data: products = [] } = useQuery({
    queryKey: ["admin-reward-products", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("reward_products" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("created_at");
      return (data ?? []) as RewardProduct[];
    },
    enabled: !!organization?.id,
  });

  const [productDialog, setProductDialog] = useState(false);
  const [editProduct, setEditProduct] = useState<RewardProduct | null>(null);
  const [pForm, setPForm] = useState({ name: "", description: "", coin_cost: 50, stock: "", image_url: "", is_active: true });

  const openNewProduct = () => {
    setEditProduct(null);
    setPForm({ name: "", description: "", coin_cost: 50, stock: "", image_url: "", is_active: true });
    setProductDialog(true);
  };

  const openEditProduct = (p: RewardProduct) => {
    setEditProduct(p);
    setPForm({
      name: p.name,
      description: p.description || "",
      coin_cost: p.coin_cost,
      stock: p.stock?.toString() ?? "",
      image_url: p.image_url || "",
      is_active: p.is_active,
    });
    setProductDialog(true);
  };

  const saveProductMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        organization_id: organization!.id,
        name: pForm.name,
        description: pForm.description || null,
        coin_cost: pForm.coin_cost,
        stock: pForm.stock ? parseInt(pForm.stock) : null,
        image_url: pForm.image_url || null,
        is_active: pForm.is_active,
      };
      if (editProduct?.id) {
        const { error } = await supabase.from("reward_products" as any).update(payload).eq("id", editProduct.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("reward_products" as any).insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-reward-products"] });
      setProductDialog(false);
      toast.success(t("admin:toast.rewardSaved"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("rewards.couldNotSave")); },
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("reward_products" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-reward-products"] });
      toast.success(t("admin:toast.rewardDeleted"));
    },
  });

  // Manual coin assignment
  const [manualDialog, setManualDialog] = useState(false);
  const [manualPlayerId, setManualPlayerId] = useState("");
  const [manualAmount, setManualAmount] = useState(10);
  const [manualDesc, setManualDesc] = useState("");

  const { data: allProfiles = [] } = useQuery({
    queryKey: ["admin-profiles-for-coins"],
    queryFn: async () => {
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name").order("full_name");
      return data ?? [];
    },
  });

  const assignCoinsMutation = useMutation({
    mutationFn: async () => {
      if (!manualPlayerId || !organization) throw new Error("Välj spelare");

      // Upsert player_coins
      const { data: existing } = await supabase
        .from("player_coins" as any)
        .select("*")
        .eq("user_id", manualPlayerId)
        .eq("organization_id", organization.id)
        .maybeSingle();

      const existingRecord = existing as { balance?: number; lifetime_earned?: number } | null;
      const currentBalance = existingRecord?.balance ?? 0;
      const currentLifetime = existingRecord?.lifetime_earned ?? 0;

      if (existing) {
        await supabase
          .from("player_coins" as any)
          .update({
            balance: currentBalance + manualAmount,
            lifetime_earned: manualAmount > 0 ? currentLifetime + manualAmount : currentLifetime,
            updated_at: new Date().toISOString(),
          })
          .eq("user_id", manualPlayerId)
          .eq("organization_id", organization.id);
      } else {
        await supabase
          .from("player_coins" as any)
          .insert({
            user_id: manualPlayerId,
            organization_id: organization.id,
            balance: manualAmount,
            lifetime_earned: manualAmount > 0 ? manualAmount : 0,
          });
      }

      // Create transaction
      await supabase.from("coin_transactions" as any).insert({
        user_id: manualPlayerId,
        organization_id: organization.id,
        amount: manualAmount,
        type: manualAmount >= 0 ? "earned_manual" : "admin_adjustment",
        description: manualDesc || `Manuell tilldelning av admin`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-coin-transactions"] });
      setManualDialog(false);
      setManualPlayerId("");
      setManualAmount(10);
      setManualDesc("");
      toast.success(t("admin:toast.coinsGranted"));
    },
    onError: (e: Error) => toast.error(e.message || "Fel"),
  });

  // All transactions
  const { data: allTransactions = [] } = useQuery({
    queryKey: ["admin-coin-transactions", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("coin_transactions" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false })
        .limit(100);
      return (data ?? []) as CoinTransaction[];
    },
    enabled: !!organization?.id,
  });

  const typeLabels: Record<string, string> = {
    earned_booking: "Bokning",
    earned_match: "Match",
    earned_tournament: "Turnering",
    earned_manual: "Manuell",
    spent_reward: "Inlöst",
    admin_adjustment: "Justering",
  };

  const profileMap = Object.fromEntries(allProfiles.map((p) => [p.id, p.full_name || "Okänd"]));

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold">Belöningssystem</h2>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config" className="gap-1"><Coins className="h-3.5 w-3.5" /> Konfiguration</TabsTrigger>
          <TabsTrigger value="products" className="gap-1"><Gift className="h-3.5 w-3.5" /> Belöningar</TabsTrigger>
          <TabsTrigger value="assign" className="gap-1"><Users className="h-3.5 w-3.5" /> Tilldela</TabsTrigger>
          <TabsTrigger value="history" className="gap-1"><History className="h-3.5 w-3.5" /> Historik</TabsTrigger>
        </TabsList>

        {/* Config tab */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <Label>Belöningssystem aktivt</Label>
                <Switch
                  checked={currentConfig.is_enabled ?? false}
                  onCheckedChange={(v) => setConfigForm((p) => ({ ...p, is_enabled: v }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Coins per bokning</Label>
                  <NumberInput
                    value={currentConfig.coins_per_booking ?? 10}
                    onValueChange={(v) => setConfigForm((p) => ({ ...p, coins_per_booking: v || 0 }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Coins per match</Label>
                  <NumberInput
                    value={currentConfig.coins_per_match ?? 5}
                    onValueChange={(v) => setConfigForm((p) => ({ ...p, coins_per_match: v || 0 }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Coins per turnering</Label>
                  <NumberInput
                    value={currentConfig.coins_per_tournament ?? 20}
                    onValueChange={(v) => setConfigForm((p) => ({ ...p, coins_per_tournament: v || 0 }))}
                  />
                </div>
              </div>
              <Button onClick={() => saveConfigMutation.mutate()} disabled={saveConfigMutation.isPending}>
                {saveConfigMutation.isPending ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Spara
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products tab */}
        <TabsContent value="products" className="space-y-3">
          <Button size="sm" onClick={openNewProduct}>
            <Plus className="h-4 w-4 mr-1" /> Ny belöning
          </Button>

          {products.length === 0 ? (
            <p className="text-sm text-muted-foreground">Inga belöningar skapade ännu.</p>
          ) : (
            <div className="space-y-2">
              {products.map((p) => (
                <Card key={p.id}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{p.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{p.coin_cost} 🪙</span>
                      {p.stock !== null && <span className="text-xs text-muted-foreground ml-2">(lager: {p.stock})</span>}
                      {!p.is_active && <Badge variant="secondary" className="ml-2 text-xs">Inaktiv</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditProduct(p)}>Redigera</Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteProductMutation.mutate(p.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Assign tab */}
        <TabsContent value="assign" className="space-y-3">
          <Button size="sm" onClick={() => setManualDialog(true)}>
            <Coins className="h-4 w-4 mr-1" /> Tilldela coins
          </Button>
          <p className="text-xs text-muted-foreground">Tilldela coins manuellt till en spelare, t.ex. som bonus.</p>
        </TabsContent>

        {/* History tab */}
        <TabsContent value="history" className="space-y-2">
          {allTransactions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Inga transaktioner ännu.</p>
          ) : (
            <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
              {allTransactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant={tx.amount > 0 ? "default" : "secondary"} className="text-xs shrink-0">
                      {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                    </Badge>
                    <span className="truncate text-muted-foreground">{profileMap[tx.user_id] || tx.user_id.slice(0, 8)}</span>
                    <span className="text-xs text-muted-foreground">{typeLabels[tx.type] ?? tx.type}</span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {format(new Date(tx.created_at), "d MMM HH:mm", { locale: sv })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Product dialog */}
      <Dialog open={productDialog} onOpenChange={setProductDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editProduct ? "Redigera belöning" : "Ny belöning"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Namn</Label>
              <Input value={pForm.name} onChange={(e) => setPForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Beskrivning</Label>
              <Textarea value={pForm.description} onChange={(e) => setPForm((p) => ({ ...p, description: e.target.value }))} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Kostnad (coins)</Label>
                <NumberInput value={pForm.coin_cost} onValueChange={(v) => setPForm((p) => ({ ...p, coin_cost: v }))} />
              </div>
              <div className="space-y-1">
                <Label>Lager (tom = obegränsat)</Label>
                <Input value={pForm.stock} onChange={(e) => setPForm((p) => ({ ...p, stock: e.target.value }))} placeholder="∞" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Bild-URL (valfritt)</Label>
              <Input value={pForm.image_url} onChange={(e) => setPForm((p) => ({ ...p, image_url: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={pForm.is_active} onCheckedChange={(v) => setPForm((p) => ({ ...p, is_active: v }))} />
              <Label>Aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => saveProductMutation.mutate()} disabled={!pForm.name || saveProductMutation.isPending}>
              {saveProductMutation.isPending ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : null}
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual assign dialog */}
      <Dialog open={manualDialog} onOpenChange={setManualDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tilldela coins manuellt</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Spelare</Label>
              <Select value={manualPlayerId} onValueChange={setManualPlayerId}>
                <SelectTrigger><SelectValue placeholder="Välj spelare..." /></SelectTrigger>
                <SelectContent>
                  {allProfiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name || p.id.slice(0, 8)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Antal coins</Label>
              <NumberInput value={manualAmount} onValueChange={(v) => setManualAmount(v)} />
            </div>
            <div className="space-y-1">
              <Label>Beskrivning (valfritt)</Label>
              <Input value={manualDesc} onChange={(e) => setManualDesc(e.target.value)} placeholder="T.ex. Bonus för event" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => assignCoinsMutation.mutate()} disabled={!manualPlayerId || assignCoinsMutation.isPending}>
              {assignCoinsMutation.isPending ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : <Coins className="h-4 w-4 mr-1" />}
              Tilldela
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
